import { box, lerpBox, scaleBox, type Box } from '../geometry/box';
import type { DetectionKind } from '../detection/types';

export type TrackKind = DetectionKind | 'manual';

export interface TrackSample {
  /** Source timestamp in seconds. */
  readonly t: number;
  /** Normalized source-frame box. */
  readonly box: Box;
  /** Detector confidence (1 for user keyframes). */
  readonly score: number;
}

export interface Track {
  readonly id: string;
  readonly kind: TrackKind;
  /** Sorted by `t`, never empty. */
  readonly samples: readonly TrackSample[];
  /**
   * The only time the track may show at, when it is cut to one clip of an
   * edit: a box held past the clip's edge would land on the next clip's picture.
   */
  readonly within?: { readonly start: number; readonly end: number };
}

export const trackStart = (track: Track): number => track.samples[0]!.t;
export const trackEnd = (track: Track): number => track.samples.at(-1)!.t;

export interface SampleOptions {
  /** How long a box stays visible before the first / after the last sample. */
  readonly holdSec: number;
  /** Gaps up to this long are bridged by interpolation (missed detections). */
  readonly maxInterpolationGapSec: number;
}

export const DEFAULT_SAMPLE_OPTIONS: SampleOptions = { holdSec: 0.35, maxInterpolationGapSec: 1.5 };

/** Index of the last sample with `t <= time`, or -1. */
function lowerIndex(samples: readonly TrackSample[], time: number): number {
  let lo = 0;
  let hi = samples.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (samples[mid]!.t <= time) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
}

/**
 * Box of a track at an arbitrary time. Detection only runs a few times per second,
 * so this interpolates between samples and holds a little past the ends: in privacy
 * work a slightly long mask is fine, a single exposed frame is not.
 */
export function boxAtTime(track: Track, time: number, options: SampleOptions = DEFAULT_SAMPLE_OPTIONS): Box | null {
  const { samples, within } = track;
  if (within && (time < within.start || time >= within.end)) return null;
  const index = lowerIndex(samples, time);
  if (index === -1) {
    const first = samples[0]!;
    return first.t - time <= options.holdSec ? first.box : null;
  }
  const previous = samples[index]!;
  const next = samples[index + 1];
  if (!next) return time - previous.t <= options.holdSec ? previous.box : null;
  const gap = next.t - previous.t;
  if (gap <= options.maxInterpolationGapSec) return lerpBox(previous.box, next.box, gap > 0 ? (time - previous.t) / gap : 0);
  if (time - previous.t <= options.holdSec) return previous.box;
  if (next.t - time <= options.holdSec) return next.box;
  return null;
}

/** Light temporal smoothing of box geometry to remove detector jitter. */
export function smoothSamples(samples: readonly TrackSample[], radius = 1): TrackSample[] {
  if (samples.length <= 2 || radius <= 0) return [...samples];
  return samples.map((sample, i) => {
    let x = 0;
    let y = 0;
    let w = 0;
    let h = 0;
    let n = 0;
    for (let j = Math.max(0, i - radius); j <= Math.min(samples.length - 1, i + radius); j++) {
      const b = samples[j]!.box;
      x += b.x + b.w / 2;
      y += b.y + b.h / 2;
      w += b.w;
      h += b.h;
      n++;
    }
    const cw = w / n;
    const ch = h / n;
    return { ...sample, box: box(x / n - cw / 2, y / n - ch / 2, cw, ch) };
  });
}

/** Box used to render a mask: padded so hair, ears and plate frames are covered too. */
export const maskBox = (b: Box, padding: number): Box => scaleBox(b, 1 + padding);

/** The most representative sample (confident and large) — used for thumbnails. */
export function representativeSample(track: Track): TrackSample {
  let best = track.samples[0]!;
  let bestValue = -Infinity;
  for (const sample of track.samples) {
    const value = sample.score * Math.sqrt(sample.box.w * sample.box.h);
    if (value > bestValue) {
      bestValue = value;
      best = sample;
    }
  }
  return best;
}

export interface TrackMask {
  readonly box: Box;
  readonly kind: TrackKind;
}

/** All masks visible at `time` (source seconds). */
export function masksAtTime(tracks: readonly Track[], time: number, options: SampleOptions = DEFAULT_SAMPLE_OPTIONS): TrackMask[] {
  const masks: TrackMask[] = [];
  for (const track of tracks) {
    if (time < trackStart(track) - options.holdSec || time > trackEnd(track) + options.holdSec) continue;
    const b = boxAtTime(track, time, options);
    if (b) masks.push({ box: b, kind: track.kind });
  }
  return masks;
}
